import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useId, useState } from 'react';

import type { Activity, CompanyListFilters, Federation } from '../../../../utils/companyListFilters';
import { ACTIVITIES, DATE_FIN_CARTE_OPTIONS, FEDERATIONS } from '../../../../utils/companyListFilters';
import TriStateCheckbox from '../../../common/TriStateCheckbox';

const FEDERATION_LABELS: Record<Federation, string> = { fnaim: 'FNAIM', unis: 'UNIS', snpi: 'SNPI' };

type TextFilter = 'minFederations' | 'minActivities' | 'nbLotsHabOver' | 'nbLotsHabUnder' | 'nbLotsTotalOver' | 'nbLotsTotalUnder' | 'dateFinCarteFilter';

interface CompanyListFiltersPanelProps {
  filters: CompanyListFilters;
  onChange: React.Dispatch<React.SetStateAction<CompanyListFilters>>;
  /** Filters of the page itself, shown next to « Date fin carte ». */
  children?: React.ReactNode;
}

/** Filters of the company lists (federations, activities, lots, card end date, negative keywords). */
export default function CompanyListFiltersPanel({ filters, onChange, children }: CompanyListFiltersPanelProps) {
  const id = useId();
  const [negativeKeywordInput, setNegativeKeywordInput] = useState('');

  const setText = (name: TextFilter) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value } = e.target;
    onChange((prev) => ({ ...prev, [name]: value }));
  };

  const addNegativeKeyword = () => {
    const keyword = negativeKeywordInput.trim().toLowerCase();
    if (keyword) {
      onChange((prev) => (prev.negativeKeywords.includes(keyword)
        ? prev
        : { ...prev, negativeKeywords: [...prev.negativeKeywords, keyword] }));
      setNegativeKeywordInput('');
    }
  };

  const removeNegativeKeyword = (keyword: string) => {
    onChange((prev) => ({ ...prev, negativeKeywords: prev.negativeKeywords.filter((k) => k !== keyword) }));
  };

  const lotsInputs = (
    title: string,
    over: 'nbLotsHabOver' | 'nbLotsTotalOver',
    under: 'nbLotsHabUnder' | 'nbLotsTotalUnder',
  ) => (
    <div>
      <h3 className="mb-2 font-semibold">{title}</h3>
      <input
        type="number"
        name={over}
        value={filters[over]}
        onChange={setText(over)}
        placeholder="Plus de"
        aria-label={`${title} : plus de`}
        className="w-full rounded border p-2"
      />
      <input
        type="number"
        name={under}
        value={filters[under]}
        onChange={setText(under)}
        placeholder="Moins de"
        aria-label={`${title} : moins de`}
        className="mt-2 w-full rounded border p-2"
      />
    </div>
  );

  return (
    <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-4">
      <div className="flex flex-col">
        <h3 className="mb-2 font-semibold">Fédération</h3>
        {FEDERATIONS.map((federation) => (
          <TriStateCheckbox
            key={federation}
            label={FEDERATION_LABELS[federation]}
            state={filters.federations[federation]}
            onChange={(newState) => onChange((prev) => ({
              ...prev,
              federations: { ...prev.federations, [federation]: newState },
            }))}
          />
        ))}
        <div className="grow" />
        <div className="mt-2">
          <label htmlFor={`${id}-min-federations`} className="block">Au moins X fédération</label>
          <select
            id={`${id}-min-federations`}
            name="minFederations"
            value={filters.minFederations}
            onChange={setText('minFederations')}
            className="w-full rounded border p-2"
          >
            <option value="0">Toutes</option>
            {FEDERATIONS.map((_, index) => (
              <option key={index + 1} value={index + 1}>
                Au moins {index + 1}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex flex-col">
        <h3 className="mb-2 font-semibold">Activité (DJ)</h3>
        {ACTIVITIES.map((activity: Activity) => (
          <TriStateCheckbox
            key={activity}
            label={activity}
            state={filters.activities[activity]}
            onChange={(newState) => onChange((prev) => ({
              ...prev,
              activities: { ...prev.activities, [activity]: newState },
            }))}
          />
        ))}
        <div className="grow" />
        <div className="mt-2">
          <label htmlFor={`${id}-min-activities`} className="block">Au moins X activité</label>
          <select
            id={`${id}-min-activities`}
            name="minActivities"
            value={filters.minActivities}
            onChange={setText('minActivities')}
            className="w-full rounded border p-2"
          >
            <option value="0">Toutes</option>
            {ACTIVITIES.map((_, index) => (
              <option key={index + 1} value={index + 1}>
                Au moins {index + 1}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="col-span-1 grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-2">
        {lotsInputs('Nb lots hab', 'nbLotsHabOver', 'nbLotsHabUnder')}
        {lotsInputs('Nb lots hab / bur / com', 'nbLotsTotalOver', 'nbLotsTotalUnder')}
        <div className={children ? undefined : 'col-span-1 md:col-span-2'}>
          <label htmlFor={`${id}-date-fin-carte`} className="mb-2 block font-semibold">Date fin carte</label>
          <select
            id={`${id}-date-fin-carte`}
            name="dateFinCarteFilter"
            value={filters.dateFinCarteFilter}
            onChange={setText('dateFinCarteFilter')}
            className="w-full rounded border p-2"
          >
            {DATE_FIN_CARTE_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        {children}
      </div>

      <div className="col-span-1 mt-4 mb-6 md:col-span-4">
        <label htmlFor={`${id}-negative-keyword`} className="mb-2 block font-semibold">Filtre par mots-clés négatifs</label>
        <p className="mb-2 text-sm">Entrez un mot-clé afin que les entreprises ne soient pas listées si elles contiennent ce mot-clé dans leur nom ou SIREN.</p>
        <div className="mb-2 flex flex-wrap gap-2">
          {filters.negativeKeywords.map((keyword) => (
            <span key={keyword} className="flex items-center rounded-full bg-gray-200 px-2 py-1 text-sm">
              {keyword}
              <button
                type="button"
                onClick={() => removeNegativeKeyword(keyword)}
                aria-label={`Retirer le mot-clé ${keyword}`}
                className="ml-1 text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex">
          <input
            id={`${id}-negative-keyword`}
            type="text"
            value={negativeKeywordInput}
            onChange={(e) => setNegativeKeywordInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addNegativeKeyword()}
            placeholder="CITYA, etc."
            className="h-10 grow rounded-l border pl-2"
          />
          <button
            type="button"
            onClick={addNegativeKeyword}
            className="h-10 rounded-r bg-rgpd-blue-dark px-4 text-white transition-colors hover:bg-rgpd-blue-darker"
          >
            <span className="md:hidden">+</span>
            <span className="hidden md:block">Ajouter</span>
          </button>
        </div>
      </div>
    </div>
  );
}
